// Admin outliers API — DTOs + fetch wrappers for /api/v1/admin/listings/outliers
// and the existing strict-link migrate endpoint we re-use for re-link.
//
// Mirrors the backend contract in src/rentallens/api/admin_listings_outliers.py
// and src/rentallens/api/admin_strict_link.py. The page surfaces likely-
// wrong-linked listings; submitting a selection re-uses the same migrate
// endpoint already used by the audit/triage flow (auto_flip=true).

export type OutlierBucket = 'high' | 'low' | 'disagreement';

export interface OutlierSnapshotSummary {
  address_raw: string | null;
  postal_code: string | null;
  city: string | null;
  area_m2: number | null;
  rooms: number | null;
  monthly_rent_dkk: number | null;
}

export interface OutlierUnitSummary {
  unit_id: string;
  bbr_unit_id: string | null;
  address: string | null;
  area_m2: number | null;
  rooms: number | null;
}

export interface OutlierRow {
  listing_id: string;
  bucket: OutlierBucket;
  snapshot: OutlierSnapshotSummary;
  unit: OutlierUnitSummary;
  metric_kr_per_m2: number | null;
  disagreement_fields: string[] | null;
  relink_score: number;
}

export interface OutliersResponse {
  bucket: OutlierBucket;
  count: number;
  p99: number | null;
  p01: number | null;
  items: OutlierRow[];
}

export interface RelinkResponse {
  job_id: string;
  task: string;
  detail: string;
}

export async function fetchOutliers(
  bucket: OutlierBucket,
  limit = 50
): Promise<OutliersResponse> {
  const params = new URLSearchParams();
  params.set('bucket', bucket);
  params.set('limit', String(limit));
  const res = await fetch(
    `/api/v1/admin/listings/outliers?${params.toString()}`
  );
  if (!res.ok) throw new Error(`outliers_${res.status}`);
  return (await res.json()) as OutliersResponse;
}

// Re-link selected listings via the existing strict-link migrate
// endpoint with auto_flip=true. Threshold defaults to 'high' so the
// migration only flips listings whose T1 EXACT/HIGH (or T2 high)
// decision diverges from the current Unit — operator-safe.
export async function relinkListings(
  listingIds: string[]
): Promise<RelinkResponse> {
  const res = await fetch(
    '/api/v1/admin/listings/strict-link/migrate',
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        listing_ids: listingIds,
        auto_flip: true,
        tier_threshold: 'high'
      })
    }
  );
  if (!res.ok) {
    const detail = await res.text();
    throw new Error(`relink_${res.status}: ${detail}`);
  }
  return (await res.json()) as RelinkResponse;
}
